import { useContext, type FC } from 'react';
import type React from 'react';
import type { TFunction } from 'i18next';
import type { ReactConfirmAlertProps } from 'react-confirm-alert';
import { confirmAlert } from 'react-confirm-alert';
import type { DefaultTheme } from 'styled-components';
import { styled, css, ThemeProvider, useTheme, ThemeContext } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { getDataTestId } from '@/js/libs/utils/get-data-test-id/index.ts';

import Button from '../../Controls/Button/index.tsx';
import Icons from '../../Elements/Icons/index.tsx';
// import { palette } from '../../V2/Palette';
import { ConfirmAlertStyles } from './ConfirmAlertStyles.tsx';

export const CloseButton = styled(Icons.Cancel).attrs((props) => ({
  color: props.theme.colors.grey05_1,
}))`
  position: absolute;
  right: 15px;
  top: 0;
  bottom: 0;
  margin: auto;
  cursor: pointer;
`;

export const Body = styled.div<{ type?: 'error' }>`
  position: relative;
  width: ${({ type }) => (type ? '600px' : '400px')};
  max-width: 100%;
  padding: ${({ type }) => (type ? '20px 54px 20px 20px' : '20px')};
  text-align: left;
  background: ${({ theme }) => theme.colors.grey00_1};
  border-radius: ${({ type }) => (type ? '0 0 3px 3px' : '3px')};
  color: ${({ theme }) => theme.colors.grey100_1};
  ${({ theme }) => theme.shadows.medium};
  ${({ type }) =>
    type === 'error' &&
    css`
      border-top: 2px solid ${({ theme }) => theme.colors.critical100};
    `}
`;

export const Title = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.black_1};
`;

export const Message = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black_1};
`;

export const ButtonsContainer = styled.div<{ $active?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ $active }) => $active && '20px'};
  flex-direction: row-reverse;
`;

interface ConfirmAlertBodyProps extends Omit<ReactConfirmAlertProps, 'customUI' | 'buttons'> {
  closeText?: string;
  withoutClose?: boolean;
  theme?: DefaultTheme;
  type?: 'error';
  body?: React.ReactNode;
  buttons?: Array<{
    label: string;
    onClick: (close: () => void) => void;
  }>;
  t?: TFunction;
  onClose: (...args: unknown[]) => unknown;
}

export const ConfirmAlertBody: FC<ConfirmAlertBodyProps> = ({
  type,
  title,
  body,
  buttons,
  message,
  withoutClose,
  theme,
  closeText,
  t,
  onClose,
}: ConfirmAlertBodyProps) => {
  const fallbackTheme = useContext(ThemeContext);
  const effectiveTheme = theme ?? fallbackTheme;

  if (!effectiveTheme) {
    throw new Error('ConfirmAlert: theme is missing. Use this alert within a ThemeProvider.');
  }

  return (
    <ThemeProvider theme={effectiveTheme}>
      <ConfirmAlertStyles />
      <Body type={type} data-testid='confirm-alert-body'>
        {type && withoutClose && <CloseButton onClick={onClose} data-testid='close-button' />}
        <Title data-testid='modal-alert-title'>{title}</Title>
        <Message data-testid='modal-alert-message'>{message}</Message>
        {body && <div>{body}</div>}
        <ButtonsContainer $active={!!buttons || !withoutClose}>
          {buttons &&
            buttons.map((btn) => (
              <Button key={btn.label} onClick={() => btn.onClick(onClose)} data-testid={getDataTestId(btn.label, 'button')}>
                {btn.label}
              </Button>
            ))}
          {!withoutClose && (
            <Button onClick={onClose} data-testid='no-button'>
              {closeText || (t && t('messages:CANCEL'))}
            </Button>
          )}
        </ButtonsContainer>
      </Body>
    </ThemeProvider>
  );
};

export interface ConfirmAlertProps extends Omit<ReactConfirmAlertProps, 'customUI' | 'buttons'> {
  closeText?: string;
  withoutClose?: boolean;
  theme?: DefaultTheme;
  type?: 'error';
  body?: React.ReactNode;
  buttons?: Array<{
    label: string;
    onClick: (close: () => void) => void;
  }>;
  t?: TFunction;
}

const styledConfirmAlert = (options: ConfirmAlertProps): void =>
  confirmAlert({
    ...options,
    buttons: options.buttons as Array<{
      label: string;
      onClick: () => void;
    }>,
    customUI: ({ title, message, onClose }) => (
      <ThemeContext.Provider value={options.theme}>
        <ConfirmAlertBody title={title} message={message} onClose={onClose} {...options} />
      </ThemeContext.Provider>
    ),
  });

/**
 * @deprecated Please, use `ModalDialog` instead
 * @param options
 */
function useConfirmAlert() {
  const theme = useTheme();
  return useCallbackRef((opts: ConfirmAlertProps) => styledConfirmAlert({ theme, ...opts }));
}

export default useConfirmAlert;
